import { useMemo, useState, type ReactElement } from 'react';
import { PlatformRole, type UpdateUserRequestDto, type UserDto } from '@gr/core/accounts/identity/dto';
import { useServices, useSession, useT } from '../../../app/di';
import { useAction, useResource } from '../../../shared/hooks';
import { Alert } from '../../../ui-kit/Alert/Alert';
import { Button } from '../../../ui-kit/Button/Button';
import { Dialog } from '../../../ui-kit/Dialog/Dialog';
import { Loading } from '../../../ui-kit/Loading/Loading';
import { PageHeader } from '../../../ui-kit/PageHeader/PageHeader';
import { UserPlanDialog } from '../plans';
import { NewUserForm } from './NewUserForm/NewUserForm';
import { ResetPasswordForm } from './ResetPasswordForm/ResetPasswordForm';
import { UserTable } from './UserTable/UserTable';

// IAM-UC-5, IAM-UC-6, IAM-UC-7; PLAN-UC-4 — тариф пользователя
export function UsersAdminPage(): ReactElement {
  const { api } = useServices();
  const { t, te } = useT();
  const { me } = useSession();
  const users = useResource(() => api.identity.listUsers(), [api]);
  const plans = useResource(() => api.plans.list(), [api]);
  const assignments = useResource(() => api.plans.assignments(), [api]);
  const [creating, setCreating] = useState(false);
  const [resetFor, setResetFor] = useState<UserDto | null>(null);
  const [planFor, setPlanFor] = useState<UserDto | null>(null);
  // PLAN-RULE-4: без назначения действует план по умолчанию.
  const planNameOf = useMemo(() => {
    const names = new Map((plans.data ?? []).map((plan) => [plan.id, plan.name]));
    const byUser = new Map((assignments.data ?? []).map((item) => [item.userId, item.planId]));
    const fallback = plans.data?.find((plan) => plan.isDefault)?.name ?? null;
    return (user: UserDto): string | null => {
      const planId = byUser.get(user.id);
      return planId ? (names.get(planId) ?? null) : fallback;
    };
  }, [plans.data, assignments.data]);
  const update = useAction((userId: string, patch: UpdateUserRequestDto) => api.identity.updateUser(userId, patch));

  const patch = async (user: UserDto, body: UpdateUserRequestDto): Promise<void> => {
    if ((await update.run(user.id, body)).success) await users.reload();
  };

  return (
    <div className="u-stack-lg">
      <PageHeader
        title={t('identity.users.title')}
        subtitle={t('identity.users.subtitle')}
        actions={
          <Button variant="primary" icon="plus" onClick={() => setCreating(true)}>
            {t('identity.users.create')}
          </Button>
        }
      />
      {update.error ? <Alert tone="danger">{te(update.error)}</Alert> : null}
      {users.loading && !users.data ? (
        <Loading label={t('common.loading')} />
      ) : users.error ? (
        <Alert tone="danger" title={t('common.loadFailed')}>
          <Button size="sm" onClick={() => void users.reload()}>
            {t('common.retry')}
          </Button>
        </Alert>
      ) : (
        <UserTable
          users={users.data ?? []}
          currentUserId={me?.user.id ?? null}
          pending={update.pending}
          onToggleDisabled={(user) => void patch(user, { disabled: !user.disabled })}
          onToggleAdmin={(user) => void patch(user, { platformRole: user.platformRole === PlatformRole.Admin ? PlatformRole.User : PlatformRole.Admin })}
          onResetPassword={setResetFor}
          planOf={plans.data && assignments.data ? planNameOf : undefined}
          onOpenPlan={setPlanFor}
        />
      )}
      {creating ? (
        <Dialog
          open
          title={t('identity.users.create')}
          closeLabel={t('common.close')}
          onClose={() => setCreating(false)}
          footer={
            <>
              <Button onClick={() => setCreating(false)}>{t('common.cancel')}</Button>
              <Button variant="primary" type="submit" form="create-user">
                {t('common.create')}
              </Button>
            </>
          }
        >
          <NewUserForm
            id="create-user"
            onSubmit={async (values) => {
              const result = await api.identity.createUser(values);
              if (result.success) {
                setCreating(false);
                await Promise.all([users.reload(), assignments.reload(), plans.reload()]);
              }
              return result;
            }}
          />
        </Dialog>
      ) : null}
      {resetFor ? <ResetPasswordDialog user={resetFor} onClose={() => setResetFor(null)} /> : null}
      {planFor ? (
        <UserPlanDialog
          user={planFor}
          plans={plans.data ?? []}
          onClose={() => setPlanFor(null)}
          onAssigned={() => void Promise.all([assignments.reload(), plans.reload()])}
        />
      ) : null}
    </div>
  );
}

function ResetPasswordDialog({ user, onClose }: { user: UserDto; onClose(): void }): ReactElement {
  const { api } = useServices();
  const { t } = useT();
  const [done, setDone] = useState(false);
  return (
    <Dialog
      open
      title={t('identity.users.resetTitle', { login: user.login })}
      closeLabel={t('common.close')}
      onClose={onClose}
      footer={
        done ? (
          <Button onClick={onClose}>{t('common.close')}</Button>
        ) : (
          <Button variant="primary" type="submit" form="reset-password">
            {t('common.save')}
          </Button>
        )
      }
    >
      {done ? (
        <Alert tone="success">{t('identity.users.resetDone')}</Alert>
      ) : (
        <ResetPasswordForm
          id="reset-password"
          onSubmit={async (password) => {
            const result = await api.identity.resetPassword(user.id, password);
            setDone(result.success);
            return result;
          }}
        />
      )}
    </Dialog>
  );
}
